import React from "react";
import { Box, List, ListItem, Modal, ModalDialog, Tab, TabList, Tabs, TabPanel } from "@mui/joy";
import type { TheoryTest, TheoryTestTemplate } from "../../../../api/theoryTests/TheoryTest.typed";
import { CheckCircleIcon, CancelIcon, CloseRoundedIcon } from "../../../../styles/StyledMUI";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import {
  colorTokens,
  zekayModalTabsRootSx,
  zekayModalTabListSx,
  zekayModalTabPanelSx,
  withAlpha,
} from "../../../../styles/globalStyles";
import { theoryTestResultFraction } from "../theoryTestHelpers";

type TheoryTestResultsModalProps = {
  open: boolean;
  onClose: () => void;
  titleLine: string;
  tests: TheoryTest[];
  template: TheoryTestTemplate | null;
  selectedTab: number;
  onTabChange: (index: number) => void;
};

export default function TheoryTestResultsModal({
  open,
  onClose,
  titleLine,
  tests,
  template,
  selectedTab,
  onTabChange,
}: TheoryTestResultsModalProps) {
  const compareAnswers = React.useCallback(
    (providedAnswers: string[], correctAnswers: string[]) =>
      providedAnswers.length === correctAnswers.length &&
      providedAnswers.every((a) => correctAnswers.includes(a)),
    [],
  );

  const modalHeaderSx = {
    backgroundColor: colorTokens.bg.surface,
    color: colorTokens.text.primary,
    borderBottom: "1px solid",
    borderColor: "divider",
    flexShrink: 0,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <ModalDialog
        sx={{
          backgroundColor: colorTokens.bg.surface,
          color: colorTokens.text.primary,
          overflow: "hidden",
          m: { xs: 1, sm: 2 },
          maxWidth: "95vw",
          width: { xs: "95vw", sm: "85vw", md: "80vw" },
          maxHeight: "90vh",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        <Box
          sx={{
            ...modalHeaderSx,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
          }}
        >
          <DarkTypography
            level="h3"
            colorVariant="title"
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {titleLine}
          </DarkTypography>
          <IconButtonCTA
            variant="plain"
            color="neutral"
            onClick={onClose}
            size="sm"
            aria-label="Fermer"
            sx={{
              flexShrink: 0,
              color: colorTokens.accent.primary,
              "&:hover": { backgroundColor: colorTokens.bg.surfaceElevated },
            }}
          >
            <CloseRoundedIcon />
          </IconButtonCTA>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: colorTokens.bg.surface,
            overscrollBehavior: "contain",
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, value) => onTabChange(value as number)}
            sx={zekayModalTabsRootSx}
          >
            <TabList sx={zekayModalTabListSx}>
              {tests.map((test, index) => (
                <Tab key={index}>
                  #{test.Attempt} -{" "}
                  {theoryTestResultFraction(test.Result).toLocaleString("fr-FR", {
                    style: "percent",
                  })}
                </Tab>
              ))}
            </TabList>

            {tests.map((test, index) => (
              <TabPanel key={index} value={index} sx={zekayModalTabPanelSx}>
                <List sx={{ "--List-gap": 1 }}>
                  {test.Form.map((answer, qIndex) => {
                    const templateQuestion = template?.TheoryTest?.[qIndex];
                    const isCorrect =
                      templateQuestion &&
                      compareAnswers(answer.ProvidedAnswers, templateQuestion.CorrectAnswers);
                    return (
                      <ListItem
                        key={qIndex}
                        sx={{
                          backgroundColor: colorTokens.bg.surfaceElevated,
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: isCorrect
                            ? colorTokens.status.success
                            : colorTokens.status.danger,
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          p: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 40,
                          }}
                        >
                          {isCorrect ? (
                            <CheckCircleIcon
                              sx={{ fontSize: "2rem", color: colorTokens.status.success }}
                            />
                          ) : (
                            <CancelIcon
                              sx={{ fontSize: "2rem", color: colorTokens.status.danger }}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <DarkTypography
                            level="title-sm"
                            sx={{ mb: 1, fontStyle: "italic", color: colorTokens.text.primary }}
                          >
                            {answer.QuestionText}
                          </DarkTypography>
                          <DarkTypography
                            level="body-sm"
                            sx={{ mb: 0.5, color: withAlpha(colorTokens.text.primary, 0.85) }}
                          >
                            Réponses données :
                          </DarkTypography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                            {answer.ProvidedAnswers.map((ans, aIndex) => (
                              <DarkTypography
                                key={aIndex}
                                level="body-sm"
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: "sm",
                                  color: colorTokens.text.primary,
                                  backgroundColor: templateQuestion?.CorrectAnswers.includes(ans)
                                    ? colorTokens.status.success
                                    : colorTokens.status.danger,
                                }}
                              >
                                {ans}
                              </DarkTypography>
                            ))}
                          </Box>
                          {templateQuestion && !isCorrect && (
                            <>
                              <DarkTypography
                                level="body-sm"
                                sx={{ mb: 0.5, color: withAlpha(colorTokens.text.primary, 0.85) }}
                              >
                                Réponses correctes :
                              </DarkTypography>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {templateQuestion.CorrectAnswers.map((ans, aIndex) => (
                                  <DarkTypography
                                    key={aIndex}
                                    level="body-sm"
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: "sm",
                                      color: colorTokens.text.primary,
                                      backgroundColor: colorTokens.status.success,
                                    }}
                                  >
                                    {ans}
                                  </DarkTypography>
                                ))}
                              </Box>
                            </>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </TabPanel>
            ))}
          </Tabs>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
